import { useState } from 'react';
import DSBox from '../../../design-system/DSBox';
import DSTypography from '../../../design-system/Typography/DSTypography';
import DSTextArea from '../../../design-system/DSTextArea';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import useKlydoEditableView from '../hooks/useKlydoEditableView';
import usePreviewOnClock from '../hooks/usePreviewOnClock';
import useAppContext from '../../../hooks/useAppContext';
import { PopulatedKlydo } from '../../../api/klydo';
import KlydoFileInput from './KlydoFileInput';
import KlydoColorStack from '../ColorPicker/components/KlydoColorStack';
import { Theme } from '../../../types';
import DSTooltip from '../../../design-system/DSTooltip';
import {
  KLYDO_NAME_LIMIT,
  KLYDO_SUBTITLE_LIMIT,
} from '../../../consts/textFieldsLimitations';
import ErrorDialog from '../../../components/ErrorDialog';
import {
  INCOMPLETE_PROFILE_ALERT,
  INCOMPLETE_PROFILE_REASON_ALERT,
} from '../../../consts/alerts';
import DSSwitch from '../../../design-system/DSSwitch';
import PublishModal, { PublicationTiming } from '../Publication/PublishModal';
import PublishSuccessModal from '../Publication/PublishSuccessModal';
import SpecialPropertiesModal from '../Publication/SpecialPropertiesModal';
import {
  getPublicationModalDefaults,
  getSubmitCtaState,
  SubmitCtaState,
  SUBMIT_CTA_LABEL,
} from '../Publication/publicationState';
import DSIconButton from '../../../design-system/DSIconButton';
import { CHIME_SPEC, STRIKE_SPEC, ALARM_SPEC } from '../../../consts/specs';
import { MAX_STRIKE_DURATION_SECONDS } from '../../../consts/audio';
import DSHelpOutline from '../../../design-system/DSHelpOutline';
import KlydoStatus from '../KlydosList/components/KlydoStatus';
import KlydoReviewDialog from '../KlydosList/components/KlydoReviewDialog';
import KlydoChimeInput from './KlydoChimeInput';
import KlydoThumbnailInput from './KlydoThumbnailInput';
import ConnectClockModal from './ConnectClockModal';
import PreviewUnavailableModal from './PreviewUnavailableModal';
import { ReactComponent as EyeIcon } from '../../../design-system/Icons/EyeIcon.svg';
import { ReactComponent as CheckFilledIcon } from '../../../design-system/Icons/CheckFilled.svg';
import MarkUnreadChatAlt from '@mui/icons-material/MarkUnreadChatAlt';

type KlydoEditableViewProps = {
  klydo?: PopulatedKlydo;
  setSuggestedColors: (theme: Theme[] | undefined) => void;
  onKlydoThemeChange: (theme: Theme | undefined) => void;
  suggestedColors?: Theme[];
  klydoSecureUrl: string;
  onLocalUrlChange: (url: string) => void;
  onSecuredUrlChange: (url: string) => void;
  selectedTheme: Theme | undefined;
  klydoId: string | undefined;
  onSubmitKlydo: () => void;
  /** True once the artist chose Edit & Resubmit on a reviewed klydo. */
  isEditingState?: boolean;
  onKeepEditorMounted: () => void;
};

/** Why the CTA is dead, for the states where it is not about missing fields. */
const SUBMIT_CTA_REASON: Partial<Record<SubmitCtaState, string>> = {
  [SubmitCtaState.SentToFeed]: 'This Klydo is already on its way to the Feed.',
  [SubmitCtaState.InCollection]: 'This Klydo is reviewed with its Collection.',
};

const KlydoEditableView = ({
  klydo,
  setSuggestedColors,
  onKlydoThemeChange,
  suggestedColors,
  onLocalUrlChange,
  onSecuredUrlChange,
  klydoSecureUrl,
  selectedTheme,
  klydoId,
  onSubmitKlydo,
  isEditingState,
  onKeepEditorMounted,
}: KlydoEditableViewProps) => {
  const {
    handleFileChange,
    handleRemoveFile,
    handleSubmitToReview,
    reviewComment,
    setReviewComment,
    klydoTitle,
    setKlydoTitle,
    klydoSubtitle,
    setKlydoSubtitle,
    isLoadingUrl,
    spacing,
    isSubmitButtonDisabled,
    fileName,
    handleMissingFields,
    onChangeColor,
    profileIncompleteErrorDialogOpen,
    handleCloseErrorDialog,
    onEditProfile,
    submitDialogOpen,
    onApprovedSubmit,
    publicUnsupportedProperties,
    specialPropertiesDialogOpen,
    isRequestingPublication,
    onConfirmSpecialProperties,
    onClosedSpecialPropertiesDialog,
    onClosedSubmitDialog,
    submitSuccess,
    onCloseSubmitSuccess,
    handleSaveTheme,
    palette,
    calendarEvent,
    soundMode,
    onSoundModeChange,
    playOnceFrequency,
    onPlayOnceFrequencyChange,
    chimeUrl,
    chimeName,
    handleChimeFileChange,
    handleRemoveChimeAndStrike,
    isLoadingChime,
    isChimePlaying,
    handleChimePlayPause,
    strikeUrl,
    strikeName,
    isLoadingStrike,
    handleStrikeFileChange,
    handleRemoveStrike,
    isStrikePlaying,
    handleStrikePlayPause,
    alarmEnabled,
    handleAlarmToggle,
    alarmUrl,
    alarmName,
    isLoadingAlarm,
    handleAlarmFileChange,
    handleRemoveAlarm,
    isAlarmPlaying,
    handleAlarmPlayPause,
    klydoDurationInSeconds,
    previewFrameSeconds,
    onPreviewFrameChange,
  } = useKlydoEditableView({
    klydo,
    setSuggestedColors,
    onLocalUrlChange,
    onSecuredUrlChange,
    klydoSecureUrl,
    onKlydoThemeChange,
    selectedTheme,
    klydoId,
    onSubmitKlydo,
    onKeepEditorMounted,
  });

  // The same defaults the read-only view uses, so one modal does not behave two
  // ways depending on which screen opened it.
  const publicationDefaults = getPublicationModalDefaults(klydo, calendarEvent);

  const { isAdmin } = useAppContext();

  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const reviewStatus = klydo?.public?.reviewStatus;
  const reviewerMessage = reviewStatus?.message?.trim();

  const {
    canPreview,
    buttonText,
    isModalOpen,
    isConnecting,
    isLoading: isPreviewLoading,
    isDisabled: isPreviewDisabled,
    tooltipText: previewTooltipText,
    handlePreviewClick,
    handleConnectClock,
    handleCloseModal,
    previewUnavailableReason,
    handleUpdateClock,
    handleClosePreviewUnavailable,
    isUpdating,
  } = usePreviewOnClock({ klydoId });

  /**
   * The CTA is the action AND the status: it asks where the klydo should go, and
   * once it has gone somewhere it names the destination and goes dead. Only the
   * live state keeps the missing-fields tooltip — on the other two the fields are
   * no longer why the button is disabled.
   */
  const submitCtaState = getSubmitCtaState(klydo);
  const isSubmitCtaActionable = submitCtaState === SubmitCtaState.Ready;
  const submitCtaTooltip = isSubmitCtaActionable
    ? handleMissingFields()
    : SUBMIT_CTA_REASON[submitCtaState];

  return (
    <>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: spacing(1),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <DSTypography variant="h4">Edit</DSTypography>
          <DSBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
              gap: spacing(2),
            }}
          >
            {canPreview && (
              <DSTooltip
                title={previewTooltipText || handleMissingFields()}
                placement="bottom"
              >
                <DSSecondaryButton
                  variant="contained"
                  startIcon={<EyeIcon />}
                  onClick={handlePreviewClick}
                  disabled={
                    isSubmitButtonDisabled ||
                    isPreviewLoading ||
                    isPreviewDisabled
                  }
                  size="small"
                >
                  {buttonText}
                </DSSecondaryButton>
              </DSTooltip>
            )}
            {reviewerMessage && (
              <DSTooltip
                title={'Review and response rejected message'}
                placement="bottom"
              >
                <DSSecondaryButton
                  variant="contained"
                  startIcon={<MarkUnreadChatAlt />}
                  onClick={() => setIsReviewDialogOpen(true)}
                  size="small"
                >
                  Review
                </DSSecondaryButton>
              </DSTooltip>
            )}
            <DSTooltip title={submitCtaTooltip} placement="bottom">
              <DSPrimaryButton
                disabled={isSubmitButtonDisabled || !isSubmitCtaActionable}
                onClick={handleSubmitToReview}
                size="small"
                startIcon={<CheckFilledIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {SUBMIT_CTA_LABEL[submitCtaState]}
              </DSPrimaryButton>
            </DSTooltip>
          </DSBox>
        </DSBox>
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: spacing(2),
            flexDirection: 'row',
          }}
        >
          <DSTypography variant="body1">
            Last Update{' '}
            {new Date(klydo?.updatedAt || new Date()).toLocaleDateString(
              undefined,
              {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
              },
            )}
          </DSTypography>
          {!isEditingState && <KlydoStatus klydo={klydo} longPendingLabel />}
        </DSBox>
        {isReviewDialogOpen && reviewStatus && (
          <KlydoReviewDialog
            reviewStatus={reviewStatus}
            comment={reviewComment}
            onCommentChange={setReviewComment}
            onClose={() => setIsReviewDialogOpen(false)}
          />
        )}

        {calendarEvent && (
          <DSBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              gap: spacing(2),
            }}
          >
            <DSTypography variant="body1" sx={{ color: palette.text.primary }}>
              Element design for{' '}
              <DSTypography
                variant="value"
                sx={{ color: palette.text.primary }}
              >
                {calendarEvent.name}
              </DSTypography>{' '}
              feed
            </DSTypography>

            {calendarEvent.emojis && (
              <DSBox sx={{ gap: spacing(2), display: 'flex' }}>
                {calendarEvent.emojis.map((emoji, index) => (
                  <DSTypography sx={{ fontSize: spacing(2.5) }} key={index}>
                    {emoji}
                  </DSTypography>
                ))}
              </DSBox>
            )}
          </DSBox>
        )}
      </DSBox>

      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          gap: spacing(3),
          minHeight: 0,
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(3),
            flex: 1,
            overflowY: 'auto',
            paddingRight: spacing(2.5),
            marginRight: `-${spacing(2.5)}`,
            minHeight: 0,
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing(1),
            }}
          >
            <DSTypography variant="label">
              {`*Choose a public name for your Klydo (max ${KLYDO_NAME_LIMIT} characters)`}
            </DSTypography>
            <DSTextArea
              placeholder="Name your Klydo"
              value={klydoTitle}
              fullWidth
              required
              maxRows={2}
              slotProps={{
                input: {
                  inputProps: { maxLength: KLYDO_NAME_LIMIT },
                },
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setKlydoTitle(event.target.value);
              }}
            />
          </DSBox>
          {isAdmin && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(1),
              }}
            >
              <DSTypography variant="label">
                {`Subtitle (max ${KLYDO_SUBTITLE_LIMIT} characters)`}
              </DSTypography>
              <DSTextArea
                placeholder="Add a subtitle"
                value={klydoSubtitle}
                maxRows={3}
                fullWidth
                slotProps={{
                  input: {
                    inputProps: { maxLength: KLYDO_SUBTITLE_LIMIT },
                  },
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setKlydoSubtitle(event.target.value);
                }}
              />
            </DSBox>
          )}
          <KlydoFileInput
            onFileChange={handleFileChange}
            klydoSecureUrl={klydoSecureUrl}
            onRemoveFile={handleRemoveFile}
            fileName={fileName}
            isLoadingUrl={isLoadingUrl}
            soundMode={soundMode}
            onSoundModeChange={onSoundModeChange}
            playOnceFrequency={playOnceFrequency}
            onPlayOnceFrequencyChange={onPlayOnceFrequencyChange}
          />
          <KlydoThumbnailInput
            loopUrl={klydoSecureUrl}
            offsetSeconds={previewFrameSeconds}
            durationInSeconds={klydoDurationInSeconds}
            onOffsetChange={onPreviewFrameChange}
          />
          <KlydoChimeInput
            onFileChange={handleChimeFileChange}
            onRemoveFile={handleRemoveChimeAndStrike}
            chimeUrl={chimeUrl}
            chimeName={chimeName}
            isLoading={isLoadingChime}
            isPlaying={isChimePlaying}
            onPlayPause={handleChimePlayPause}
            tooltip={CHIME_SPEC}
          />
          {(chimeUrl || strikeUrl) && (
            <KlydoChimeInput
              onFileChange={handleStrikeFileChange}
              onRemoveFile={handleRemoveStrike}
              chimeUrl={strikeUrl}
              chimeName={strikeName}
              isLoading={isLoadingStrike}
              isPlaying={isStrikePlaying}
              onPlayPause={handleStrikePlayPause}
              label="Upload Strike Tone (required)"
              tooltip={STRIKE_SPEC}
              maxDurationSeconds={
                isAdmin ? undefined : MAX_STRIKE_DURATION_SECONDS
              }
            />
          )}
          {isAdmin && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(1),
              }}
            >
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <DSTypography variant="label">Alarm</DSTypography>
                  <DSTooltip title={ALARM_SPEC}>
                    <DSIconButton
                      sx={{
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: palette.text.primary,
                        },
                        color: palette.text.secondary,
                        transition: 'color 0.3s ease',
                      }}
                    >
                      <DSHelpOutline />
                    </DSIconButton>
                  </DSTooltip>
                </DSBox>
                <DSSwitch
                  disabled={!klydo}
                  checked={alarmEnabled}
                  onChange={handleAlarmToggle}
                />
              </DSBox>
              {alarmEnabled && (
                <KlydoChimeInput
                  onFileChange={handleAlarmFileChange}
                  onRemoveFile={handleRemoveAlarm}
                  chimeUrl={alarmUrl}
                  chimeName={alarmName}
                  isLoading={isLoadingAlarm}
                  isPlaying={isAlarmPlaying}
                  onPlayPause={handleAlarmPlayPause}
                  label="Upload Alarm sound"
                  tooltip={ALARM_SPEC}
                />
              )}
            </DSBox>
          )}
          <DSBox
            sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}
          >
            <DSTypography variant="label">
              *Choose a color palette for your Klydo
            </DSTypography>
            <KlydoColorStack
              themes={suggestedColors}
              setTheme={setSuggestedColors}
              onChangeTheme={onKlydoThemeChange}
              onChangeColor={onChangeColor}
              addThemeButtonDisabled={!klydoSecureUrl}
              handleSaveTheme={handleSaveTheme}
              currentTheme={selectedTheme}
            />
          </DSBox>
        </DSBox>
      </DSBox>
      <ErrorDialog
        errorTitle={INCOMPLETE_PROFILE_ALERT}
        errorReason={INCOMPLETE_PROFILE_REASON_ALERT}
        note="Don't worry, your work is saved."
        isErrorDialogOpen={profileIncompleteErrorDialogOpen}
        handleClose={handleCloseErrorDialog}
        onClick={onEditProfile}
      />
      <PublishModal
        open={submitDialogOpen}
        onClose={onClosedSubmitDialog}
        onConfirm={onApprovedSubmit}
        // A draft is the only klydo that can still choose between the Feed and a
        // Collection, so this is the one call site that asks.
        askDestination
        defaultSelection={
          publicationDefaults.isScheduledForLater
            ? PublicationTiming.Scheduled
            : PublicationTiming.Automatic
        }
        prefilledTimestamp={publicationDefaults.prefilledTimestamp}
      />
      {/* Between the destination dialog and the request: the Feed strips these,
          a Collection does not. */}
      <SpecialPropertiesModal
        open={specialPropertiesDialogOpen}
        properties={publicUnsupportedProperties}
        onClose={onClosedSpecialPropertiesDialog}
        onConfirm={onConfirmSpecialProperties}
        isSubmitting={isRequestingPublication}
      />
      <PublishSuccessModal
        success={submitSuccess}
        onClose={onCloseSubmitSuccess}
      />
      <ConnectClockModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onConnect={handleConnectClock}
        isLoading={isConnecting}
      />
      {previewUnavailableReason && (
        <PreviewUnavailableModal
          open={!!previewUnavailableReason}
          onClose={handleClosePreviewUnavailable}
          reason={previewUnavailableReason}
          onUpdate={handleUpdateClock}
          isUpdating={isUpdating}
        />
      )}
    </>
  );
};

export default KlydoEditableView;
